import React, { useState, useEffect } from "react";
import { Formik } from "formik";
import { Col, Form, Button } from "react-bootstrap";
import "./KurvChangePassword.scss";
import KurvFooter from "./KurvFooter";
import { ResetPasswordTokenVerify, ResetPassword } from "../../api";
import Loader from "../loader/loader";
import { Link } from "react-router-dom";
import { createBrowserHistory } from "history";
import * as yup from "yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faArrowLeft, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import toast, { Toaster } from "react-hot-toast";

const schema = yup.object({
    email: yup
        .string()
        .trim()
        .email("* The Email Must Be A Valid Email Address.")
        .required("* Email-Address is Required"),
    password: yup
        .string()
        .trim()
        .required("* Password Is Required")
        .min(14, "Password must be at least 14 characters.")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter.")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter.")
        .matches(/[0-9]/, "Password must contain at least one number.")
        .matches(/[\-!@#$%^&*()_+\[\]{}|;:,.<>?]/, "Password must contain at least one special character."),
    password_confirmation: yup
        .string()
        .trim()
        .required("* Confirm Password Is Required")
        .min(14, "Password must be at least 14 characters.")
        .oneOf(
            [yup.ref("password"), null],
            "The Password confirmation does not match.",
        ),
});

const KurvChangePassword = (props) => {
    const token = props.match.params.token;
    const history = props.history;
    
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [showLoginLink, setShowLoginLink] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [formData, setFormData] = useState({
        email: "",
        password: "",
        password_confirmation: "",
    });
    const [errors, setErrors] = useState([]);
    const [disableResetBtn, setDisableResetBtn] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [tokenExpired, setTokenExpired] = useState(false);

    useEffect(() => {
        const postData = { token };
        ResetPasswordTokenVerify(postData).then((res) => {
            if (res.status === 200) {
                setFormData(prev => ({
                    ...prev,
                    email: res.data.email
                }));
            } else {
                const errorMsg = res.message || 'Token verification failed. Please request a new password reset.';
                setErrorMessage(errorMsg);
                setTokenExpired(true);
                toast.error(errorMsg);
                setTimeout(() => {
                    const browserHistory = createBrowserHistory({
                        forceRefresh: true,
                    });
                    browserHistory.push("/");
                }, 5000);
            }
        }).catch((error) => {
            const errorMsg = error.message || 'An error occurred while verifying the token. Please try again.';
            setErrorMessage(errorMsg);
            setTokenExpired(true);
            toast.error(errorMsg);
            setTimeout(() => {
                const browserHistory = createBrowserHistory({
                    forceRefresh: true,
                });
                browserHistory.push("/");
            }, 5000);
        });
    }, [token]);

    const updateChange = (evt) => {
        const targetName = evt.target.name;
        if (targetName === "") {
            return false;
        }
        setFormData(prev => ({
            ...prev,
            [targetName]: evt.target.value.trim()
        }));
        setErrorMessage("");
    };

    const handleChangePassword = (values) => {
        setShowSpinner(true);

        const postData = {
            token,
            email: values.email,
            password: values.password,
            password_confirmation: values.password_confirmation,
        };
        
        ResetPassword(postData).then((res) => {
            if (res.status === 200) {
                setShowSpinner(false);
                setShowLoginLink(true);
                setErrorMessage("Password Reset Successfully.");
                toast.success("Password reset successfully!");
                setTimeout(() => {
                    history.push("/");
                }, 2000);
            } else {
                setShowSpinner(false);
                setErrorMessage(res.message);
                toast.error(res.message || "Failed to reset password. Please try again.");
            }
        }).catch((error) => {
            setShowSpinner(false);
            const validationErrors = error?.data?.errors;
            const firstFieldError = validationErrors
                ? Object.values(validationErrors)[0]?.[0]
                : null;
            const errorMsg =
                firstFieldError ||
                error?.data?.message ||
                error?.message ||
                "An error occurred while resetting the password. Please try again.";
            setErrorMessage(errorMsg);
            toast.error(errorMsg);
        });
    };

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordVisible(!confirmPasswordVisible);
    };

    return (
        <div className="kurv-forgot-password-page">
            <div className="hero-image-background" />
            <div className="kurv-forgot-password-container">
                <div className="kurv-logo">
                    <img 
                        src="/images/kurv-agent/kurv-logo.svg" 
                        alt="Kurv Agent" 
                        className="logo-image"
                    />
                </div>

                <div className="kurv-forgot-card">
                    <div className="kurv-forgot-top">
                        <Link to="/" className="kurv-back-button">
                            <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
                            <span>Back</span>
                        </Link>
                        
                        <div className="kurv-forgot-title">
                            <h2 className="title-text">Reset password</h2>
                            <p className="subtitle-text">
                                Enter your new password below to complete the reset.
                            </p>
                        </div>
                    </div>

                    <Formik
                        enableReinitialize
                        validationSchema={schema}
                        onSubmit={handleChangePassword}
                        initialValues={formData}
                        initialErrors={errors}
                        validateOnBlur={false}
                        validateOnChange={isSubmitted}
                        validate={(values) => {
                            setIsSubmitted(true);
                        }}
                    >
                        {({
                            handleSubmit,
                            handleChange,
                            values,
                            touched,
                            errors,
                        }) => (
                            <Form onSubmit={handleSubmit} className="kurv-forgot-form" onChange={updateChange}>
                                <div className="kurv-forgot-form-group">
                                    <label className="kurv-forgot-label">Email</label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={values.email ? values.email : ""}
                                        onChange={handleChange}
                                        className="kurv-forgot-input"
                                        placeholder="Email"
                                        disabled
                                    />
                                    {touched.email && errors.email && (
                                        <div className="kurv-forgot-error">{errors.email}</div>
                                    )}
                                </div>

                                <div className="kurv-forgot-form-group">
                                    <label className="kurv-forgot-label">Password</label>
                                    <div className="kurv-forgot-password-wrapper">
                                        <Form.Control
                                            type={passwordVisible ? "text" : "password"}
                                            name="password"
                                            value={values.password ? values.password : ""}
                                            onChange={handleChange}
                                            className="kurv-forgot-input"
                                            placeholder="Password"
                                        />
                                        <FontAwesomeIcon
                                            icon={passwordVisible ? faEyeSlash : faEye}
                                            className="kurv-forgot-eye-icon"
                                            onClick={togglePasswordVisibility}
                                        />
                                    </div>
                                    {touched.password && errors.password && (
                                        <div className="kurv-forgot-error">{errors.password}</div>
                                    )}
                                </div>

                                <div className="kurv-forgot-form-group">
                                    <label className="kurv-forgot-label">Confirm Password</label>
                                    <div className="kurv-forgot-password-wrapper">
                                        <Form.Control
                                            type={confirmPasswordVisible ? "text" : "password"}
                                            name="password_confirmation"
                                            value={values.password_confirmation ? values.password_confirmation : ""}
                                            onChange={handleChange}
                                            className="kurv-forgot-input"
                                            placeholder="Confirm Password"
                                        />
                                        <FontAwesomeIcon
                                            icon={confirmPasswordVisible ? faEyeSlash : faEye}
                                            className="kurv-forgot-eye-icon"
                                            onClick={toggleConfirmPasswordVisibility}
                                        />
                                    </div>
                                    {touched.password_confirmation && errors.password_confirmation && (
                                        <div className="kurv-forgot-error">{errors.password_confirmation}</div>
                                    )}
                                </div>

                                {errorMessage && (
                                    <div className="kurv-forgot-message kurv-forgot-error">
                                        {errorMessage}
                                        {tokenExpired && (
                                            <div className="kurv-forgot-token-expired">
                                                <p>Your reset link has expired. Please request a new one.</p>
                                                <Link to="/forgot-password" className="kurv-forgot-request-new-link">
                                                    Request New Reset Link
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    className="kurv-forgot-submit-button"
                                    disabled={disableResetBtn || tokenExpired}
                                >
                                    {showSpinner ? "Resetting..." : "Reset Password"}
                                </Button>

                                {showLoginLink && (
                                    <Link to="/" className="kurv-forgot-login-link">
                                        Login
                                    </Link>
                                )}
                            </Form>
                        )}
                    </Formik>
                </div>

                <a
                    href="https://myportfolio.emscorporate.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="kurv-stratus-logo"
                >
                    <img
                        src="/images/kurv-agent/myportfolio-logo.png"
                        alt="My Portfolio"
                    />
                </a>

                {showSpinner && (
                    <div className="kurv-forgot-spinner">
                        <Loader />
                    </div>
                )}
            </div>

            <KurvFooter />

            <Toaster
                position="top-right"
                toastOptions={{
                    style: {
                        borderRadius: "8px",
                        background: "rgba(40, 39, 45, 0.85)",
                        color: "#fff",
                        padding: "16px 24px",
                    },
                    duration: 5000,
                }}
            />
        </div>
    );
};

export default KurvChangePassword;